import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export const Privacy = () => (
  <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black text-white">
    <div className="container mx-auto px-4 py-12">
      <Link
        to="/"
        className="inline-flex items-center text-gray-400 hover:text-white mb-8"
      >
        <ArrowLeft className="mr-2" /> Back to Home
      </Link>

      <div className="glass-card p-8 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-gray-400 mb-8">Effective Date: February 15, 2026</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              1. Introduction
            </h2>
            <p>
              Agent FOMO ("Company," "we," "us," or "our") is committed to
              protecting the privacy and security of your personal information.
              This Privacy Policy describes how we collect, use, disclose, and
              safeguard your data when you use the Agent FOMO platform
              ("Service"). By accessing or using the Service, you consent to the
              data practices described in this policy. If you do not agree with
              these practices, please discontinue use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              2. Information We Collect
            </h2>
            <p className="mb-4">
              We may collect the following categories of personal information:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                <strong className="text-white">Contact Information:</strong>
                {" "}
                Your WhatsApp phone number, display name, and email address (if
                voluntarily shared to link your account or access specific
                features).
              </li>
              <li>
                <strong className="text-white">Location Data:</strong>{" "}
                Approximate geographic location when voluntarily shared by you,
                used to provide localized event recommendations.
              </li>
              <li>
                <strong className="text-white">
                  Preferences and Interests:
                </strong>{" "}
                Event categories, genres, and other preferences you communicate
                to the Service.
              </li>
              <li>
                <strong className="text-white">Interaction Data:</strong>{" "}
                Message history, search queries, swipes, clicks, and page views
                of event pages (used solely to train your personalized taste
                profile and never shared with third parties).
              </li>
              <li>
                <strong className="text-white">Technical Data:</strong>{" "}
                Device information, browser type, IP address, and usage
                analytics collected automatically when you access the Service
                via our website.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              3. How We Use Your Information
            </h2>
            <p className="mb-4">
              We process your personal information for the following purposes:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                To provide, operate, and maintain the Service, including
                delivering personalized event recommendations.
              </li>
              <li>
                To communicate with you regarding events, updates,
                notifications, and promotional offers relevant to your
                interests.
              </li>
              <li>
                To analyze usage patterns and improve the quality,
                functionality, and user experience of the Service.
              </li>
              <li>
                To detect, prevent, and address technical issues, fraud, or
                security threats.
              </li>
              <li>
                To comply with applicable legal obligations and enforce our
                Terms and Conditions.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              4. Legal Basis for Processing
            </h2>
            <p className="mb-4">
              We process your personal data on the following legal grounds:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-white">Consent:</strong>{" "}
                Where you have provided explicit consent for specific processing
                activities.
              </li>
              <li>
                <strong className="text-white">Contractual Necessity:</strong>
                {" "}
                Where processing is necessary to perform the Service you have
                requested.
              </li>
              <li>
                <strong className="text-white">Legitimate Interests:</strong>
                {" "}
                Where processing is necessary for our legitimate business
                interests, provided such interests do not override your
                fundamental rights and freedoms.
              </li>
              <li>
                <strong className="text-white">Legal Obligation:</strong>{" "}
                Where processing is required to comply with applicable laws or
                regulations.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              5. Data Sharing and Disclosure
            </h2>
            <p className="mb-4">
              We do not sell your personal information. We may share your data
              in the following limited circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-white">Service Providers:</strong>{" "}
                With trusted third-party vendors who assist in operating the
                Service (e.g., hosting, analytics), subject to contractual data
                protection obligations.
              </li>
              <li>
                <strong className="text-white">Legal Requirements:</strong>{" "}
                When required by law, regulation, legal process, or enforceable
                governmental request.
              </li>
              <li>
                <strong className="text-white">Aggregated Data:</strong>{" "}
                We may share anonymized, aggregated statistical data with
                partners, which cannot be used to identify you personally.
              </li>
              <li>
                <strong className="text-white">Business Transfers:</strong>{" "}
                In connection with a merger, acquisition, or sale of assets,
                your data may be transferred as part of the transaction.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              6. Data Retention
            </h2>
            <p>
              We retain your personal information only for as long as necessary
              to fulfill the purposes outlined in this Privacy Policy, unless a
              longer retention period is required or permitted by law. When your
              data is no longer needed, we will securely delete or anonymize it
              in accordance with our data retention procedures.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              7. Data Security
            </h2>
            <p>
              We implement appropriate technical and organizational security
              measures to protect your personal information against unauthorized
              access, alteration, disclosure, or destruction. However, no method
              of transmission over the Internet or electronic storage is
              completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              8. Your Rights
            </h2>
            <p className="mb-4">
              Subject to applicable law, you have the following rights regarding
              your personal data:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-white">Right of Access:</strong>{" "}
                Request a copy of the personal data we hold about you.
              </li>
              <li>
                <strong className="text-white">Right to Rectification:</strong>
                {" "}
                Request correction of inaccurate or incomplete data.
              </li>
              <li>
                <strong className="text-white">Right to Erasure:</strong>{" "}
                Request deletion of your personal data, subject to legal
                retention obligations.
              </li>
              <li>
                <strong className="text-white">
                  Right to Restrict Processing:
                </strong>{" "}
                Request that we limit the processing of your data in certain
                circumstances.
              </li>
              <li>
                <strong className="text-white">
                  Right to Data Portability:
                </strong>{" "}
                Request your data in a structured, commonly used,
                machine-readable format.
              </li>
              <li>
                <strong className="text-white">
                  Right to Withdraw Consent:
                </strong>{" "}
                Where processing is based on consent, you may withdraw it at any
                time without affecting the lawfulness of prior processing.
              </li>
            </ul>
            <p className="mt-4">
              To exercise any of these rights, please contact us using the
              information provided below. We will respond to your request within
              a reasonable timeframe and in accordance with applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              9. Cookies and Tracking Technologies
            </h2>
            <p>
              Our website may use cookies and similar tracking technologies to
              enhance your browsing experience, analyze site traffic, and
              understand usage patterns. You can manage your cookie preferences
              through your browser settings. Disabling cookies may affect the
              functionality of certain features on our website.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              10. Children's Privacy
            </h2>
            <p>
              The Service is not intended for individuals under the age of 18.
              We do not knowingly collect personal information from children. If
              we become aware that we have inadvertently collected data from a
              minor, we will take prompt steps to delete such information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              11. Changes to This Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or applicable laws. Material changes will
              be communicated through the Service or our website. Your continued
              use of the Service after publication of the revised policy
              constitutes acceptance of the updated terms. We encourage you to
              review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              12. Contact Information
            </h2>
            <p>
              If you have any questions, concerns, or requests regarding this
              Privacy Policy or our data practices, please contact us via
              WhatsApp at{" "}
              <a
                href="https://wa.me/972504244206"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:underline"
              >
                +972-50-424-4206
              </a>
              .
            </p>
          </section>

          <div className="pt-8 flex justify-center">
            <a
              href="https://wa.me/972504244206"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold transition-all hover:scale-105"
            >
              Back to Chat
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
);
